import React, { useEffect, useState } from "react";
import { Select, Option, Button, Input } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";

/**
 * 윈터 스쿨 등록 관리 > '지점별 마감관리' — 지점×프로그램(프리윈터/정규윈터)별
 *  ① 모집 상태: 등록 가능(open) / 마감 임박(soon) / 모집 마감(closed) — 바꾸면 즉시 저장
 *  ② 시작일 선택 가능 기간(startFrom~startTo): 공통 범위(프리 11/23~12/26, 정규 12/28~1/4) 안에서만 좁힐 수 있음. 비우면 공통값. 강남·대치 정규는 12/28 고정
 * 서버: GET /dashboard/payments/winterpay/status/admin · POST /dashboard/payments/winterpay/status/update { location, program, status?, startFrom?, startTo? }
 * 반영 범위: 통합 페이지(suneungsunbae.com/winterregister) 배지·대기 신청 달력, 결제 페이지(/winterpay) 프로그램 차단·시작일 달력, 서버 제출 검증.
 * '각 지점별 모집상태' 탭(상담 시트 배지, branch_recruitment_status)과는 별개.
 */

type Status = "open" | "soon" | "closed";

interface Cell {
    status: Status;
    startFrom: string; startTo: string;   // 유효값
    fixed: boolean;
    customFrom: string | null; customTo: string | null; // 설정 원본 (NULL = 공통값)
    baseFrom: string; baseTo: string;     // 공통 범위
    teacherName: string | null; updatedAt: any;
}

const STATUS_OPTIONS: { value: Status; label: string; bg: string; color: string }[] = [
    { value: "open", label: "등록 가능", bg: "#E6F4EC", color: "#087A4B" },
    { value: "soon", label: "마감 임박", bg: "#FFF1D6", color: "#A95B00" },
    { value: "closed", label: "모집 마감", bg: "#FDECEC", color: "#C0262D" },
];

const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };

const formatDateTime = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return typeof v === "string" ? v : "";
    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const mdOf = (ymd: string) => { const [, m, d] = ymd.split("-"); return `${+m}/${+d}`; };

const WinterProgramStatus: React.FC<any> = (props) => {

    const canEdit = !!(props.user && props.user.todoistApiKey);
    const [branches, setBranches] = useState<{ english: string; korean: string }[]>([]);
    const [programs, setPrograms] = useState<{ pid: string; name: string }[]>([]);
    const [detail, setDetail] = useState<{ [loc: string]: { [pid: string]: Cell } }>({});
    // 날짜 입력 중인 값 (loc:pid → {from,to}) — 둘 다 유효할 때 저장
    const [draft, setDraft] = useState<{ [key: string]: { from: string; to: string } }>({});
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch("/dashboard/payments/winterpay/status/admin");
            if (result.message === "success") {
                setBranches(result.branches || []);
                setPrograms(result.programs || []);
                setDetail(result.detail || {});
                const d: any = {};
                Object.keys(result.detail || {}).forEach(loc => Object.keys(result.detail[loc]).forEach(pid => {
                    const c = result.detail[loc][pid];
                    d[`${loc}:${pid}`] = { from: c.customFrom || "", to: c.customTo || "" };
                }));
                setDraft(d);
            } else {
                toast.error("상태를 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("상태를 불러오지 못했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const save = async (loc: string, pid: string, patch: { status?: Status; startFrom?: string | null; startTo?: string | null }) => {
        const prev = detail[loc][pid];
        // 낙관적 갱신
        setDetail(p => ({ ...p, [loc]: { ...p[loc], [pid]: { ...p[loc][pid], ...(patch.status ? { status: patch.status } : {}), ...(patch.startFrom !== undefined ? { customFrom: patch.startFrom, customTo: patch.startTo || null, startFrom: patch.startFrom || prev.baseFrom, startTo: patch.startTo || prev.baseTo } : {}) } } }));
        try {
            const result: any = await postFetch("/dashboard/payments/winterpay/status/update", { location: loc, program: pid, ...patch });
            if (result.message === "success") {
                setDetail(p => ({ ...p, [loc]: { ...p[loc], [pid]: { ...p[loc][pid], teacherName: result.teacherName, updatedAt: result.updatedAt } } }));
                toast.success("저장했습니다", { ...toastOptions, autoClose: 1200 });
            } else {
                throw new Error(result.message);
            }
        } catch (e: any) {
            console.log(e);
            setDetail(p => ({ ...p, [loc]: { ...p[loc], [pid]: prev } }));
            const msg = e && e.message === "wrongDate" ? "날짜가 공통 범위를 벗어났거나 순서가 잘못됐습니다" : e && e.message === "fixedStart" ? "시작일이 고정된 지점입니다" : "저장에 실패했습니다";
            toast.error(msg, toastOptions);
        }
    };

    // 날짜 칸 변경 — 둘 다 비면 기본값(NULL)으로 저장, 둘 다 있고 유효하면 저장, 그 외는 입력만 유지(빨간 테두리)
    const onDate = (loc: string, pid: string, which: "from" | "to", value: string) => {
        const key = `${loc}:${pid}`;
        const cur = draft[key] || { from: "", to: "" };
        const next = { ...cur, [which]: value };
        setDraft(p => ({ ...p, [key]: next }));
        const c = detail[loc][pid];
        if (!next.from && !next.to) { if (c.customFrom || c.customTo) save(loc, pid, { startFrom: null, startTo: null }); return; }
        if (next.from && next.to && next.from <= next.to && next.from >= c.baseFrom && next.to <= c.baseTo) save(loc, pid, { startFrom: next.from, startTo: next.to });
    };
    const dateValid = (loc: string, pid: string) => {
        const d = draft[`${loc}:${pid}`]; const c = detail[loc][pid];
        if (!d || (!d.from && !d.to)) return true;
        return !!d.from && !!d.to && d.from <= d.to && d.from >= c.baseFrom && d.to <= c.baseTo;
    };

    const cellStyle: React.CSSProperties = { padding: "12px 14px", borderBottom: "1px solid #e5e8eb", verticalAlign: "top", fontFamily: "Apple_R", fontSize: "14px", color: "#191f28" };

    return (
        <div style={{ width: "96%" }}>
            <div style={{ fontFamily: "Apple_B", marginTop: "50px", fontSize: "18px" }}>지점별 마감관리</div>
            <div style={{ marginTop: "10px", fontSize: "13px", color: "#6B7684", lineHeight: 1.7 }}>
                통합 페이지(suneungsunbae.com/winterregister)의 지점 배지·대기 신청과 결제 페이지(/winterpay)의 프로그램 선택·시작일 달력에 바로 반영됩니다. '각 지점별 모집상태' 탭(상담 시트용)과는 별개입니다.<br />
                시작일 선택 가능 기간은 공통 범위 안에서만 좁힐 수 있습니다 — 프리윈터 11/23~12/26, 정규윈터 12/28~1/4 (강남·대치 정규윈터는 12/28 고정). 비우면 공통값으로 돌아갑니다.
                {!canEdit && <span style={{ color: "#E8505B" }}> · 읽기 전용 (todoistApiKey 권한 필요)</span>}
            </div>

            <div style={{ marginTop: "20px", background: "white", border: "1px solid #e5e8eb", borderRadius: "8px", overflow: "hidden" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr style={{ background: "#f5f6f8" }}>
                            <th style={{ ...cellStyle, fontFamily: "Apple_B", width: "110px", textAlign: "left" }}>지점</th>
                            {programs.map(p => <th key={p.pid} style={{ ...cellStyle, fontFamily: "Apple_B", textAlign: "left" }}>{p.name}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td style={cellStyle} colSpan={1 + programs.length}>불러오는 중…</td></tr>}
                        {!loading && branches.map(b => (
                            <tr key={b.english}>
                                <td style={{ ...cellStyle, fontFamily: "Apple_B" }}>{b.korean}</td>
                                {programs.map(p => {
                                    const c = detail[b.english] && detail[b.english][p.pid];
                                    if (!c) return <td key={p.pid} style={cellStyle}>-</td>;
                                    const opt = STATUS_OPTIONS.find(o => o.value === c.status) || STATUS_OPTIONS[0];
                                    const d = draft[`${b.english}:${p.pid}`] || { from: "", to: "" };
                                    const ok = dateValid(b.english, p.pid);
                                    return (
                                        <td key={p.pid} style={cellStyle}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                                <Select
                                                    size="sm"
                                                    value={c.status}
                                                    disabled={!canEdit}
                                                    onChange={(e, v) => { if (v && v !== c.status) save(b.english, p.pid, { status: v as Status }); }}
                                                    sx={{ minWidth: "130px", fontWeight: 600, color: opt.color, backgroundColor: opt.bg, "--Select-indicatorColor": opt.color }}
                                                >
                                                    {STATUS_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                                </Select>
                                                <span style={{ fontSize: "12px", padding: "3px 8px", borderRadius: "4px", background: opt.bg, color: opt.color, fontWeight: 700 }}>{opt.label}</span>
                                            </div>
                                            <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", fontSize: "12.5px", color: "#6B7684" }}>
                                                <span>시작일</span>
                                                {c.fixed ? (
                                                    <span style={{ fontWeight: 600, color: "#333D4B" }}>{mdOf(c.startFrom)} 고정</span>
                                                ) : (
                                                    <>
                                                        <Input type="date" size="sm" value={d.from} disabled={!canEdit} slotProps={{ input: { min: c.baseFrom, max: c.baseTo } }} onChange={(e) => onDate(b.english, p.pid, "from", e.target.value)} sx={{ width: "150px", borderColor: ok ? undefined : "#E8505B" }} color={ok ? "neutral" : "danger"} />
                                                        <span>~</span>
                                                        <Input type="date" size="sm" value={d.to} disabled={!canEdit} slotProps={{ input: { min: c.baseFrom, max: c.baseTo } }} onChange={(e) => onDate(b.english, p.pid, "to", e.target.value)} sx={{ width: "150px" }} color={ok ? "neutral" : "danger"} />
                                                        {(c.customFrom || c.customTo) && canEdit && (
                                                            <Button size="sm" variant="plain" color="neutral" onClick={() => { setDraft(pr => ({ ...pr, [`${b.english}:${p.pid}`]: { from: "", to: "" } })); save(b.english, p.pid, { startFrom: null, startTo: null }); }}>기본값</Button>
                                                        )}
                                                        <span style={{ color: ok ? "#8B95A1" : "#E8505B" }}>
                                                            {ok ? `적용 중: ${mdOf(c.startFrom)} ~ ${mdOf(c.startTo)}${c.customFrom ? "" : " (공통값)"}` : `공통 범위 ${mdOf(c.baseFrom)} ~ ${mdOf(c.baseTo)} 안에서 시작 ≤ 끝으로 입력`}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <div style={{ marginTop: "6px", fontSize: "11.5px", color: "#8B95A1" }}>
                                                마지막 수정: {c.teacherName || "—"}{c.updatedAt ? ` · ${formatDateTime(c.updatedAt)}` : ""}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WinterProgramStatus;
